import { useCallback, useState } from "react"
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material"
import UploadSingleFile from "minimals-template/components/upload/UploadSingleFile"
import { importMaintenanceLibrary } from "../../controller/maintenance-library"
import { useBoundContext } from "lib/@components/binding/use-bound-context"

export function UploadLibraryDialog({ open, onClose, onSuccess }) {
    const context = useBoundContext()
    const [file, setFile] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState(null)

    const handleDrop = useCallback((acceptedFiles) => {
        const newFile = acceptedFiles[0]
        if (newFile) {
            setFile(
                Object.assign(newFile, {
                    preview: URL.createObjectURL(newFile),
                })
            )
            setError(null)
        }
    }, [])

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Upload Component Library Zip</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Upload a ZIP file containing .docx or .txt files for the maintenance library. The system will
                        automatically extract and ingest the components.
                    </Typography>

                    <UploadSingleFile
                        file={file}
                        onDrop={handleDrop}
                        header="Select library zip"
                        message="Choose a zip file"
                        accept={{
                            "application/zip": [".zip"],
                            "application/x-zip-compressed": [".zip"],
                        }}
                        error={!!error}
                        helperText={
                            error && (
                                <Typography variant="caption" color="error">
                                    {error}
                                </Typography>
                            )
                        }
                    />

                    {uploading && (
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                            <CircularProgress size={24} />
                            <Typography variant="body2">
                                Processing library... This may take a while as it uses AI for extraction.
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>
                    Cancel
                </Button>
                <Button variant="contained" color="primary" disabled={!file || uploading} onClick={handleUpload}>
                    Upload and Import
                </Button>
            </DialogActions>
        </Dialog>
    )

    function handleClose() {
        if (!uploading) {
            setFile(null)
            setError(null)
            onClose()
        }
    }

    async function handleUpload() {
        if (!file) return

        setUploading(true)
        setError(null)

        try {
            const base64 = await fileToBase64(file)
            await importMaintenanceLibrary(base64)
            if (typeof onSuccess === "function") {
                await onSuccess()
            } else if (typeof context.refresh === "function") {
                await context.refresh()
            }
            setFile(null)
            onClose()
        } catch (err) {
            console.error("Upload failed:", err)
            setError(err.message || "An unexpected error occurred during import.")
        } finally {
            setUploading(false)
        }
    }

    function fileToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => resolve(reader.result)
            reader.onerror = (error) => reject(error)
        })
    }
}
